import { Component, HostBinding, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { Observable, Subject } from 'rxjs';
import { delay, first, shareReplay, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { LoaderService } from './loader/loader.service';
import { LoaderConfig, defaultLoaderConfig } from './loader/loader.config';
import { StompWebSocketService } from '../core/services/stomp-websocket.service';
import { UserManualService } from '../shared/user-manual/user-manual.service';
import { env } from '../../environments/env';
import { ClearGlobalSearch, GlobalSearch, SearchTagsSuccess } from '@completion/actions';
import { getGlobalSearchResponse } from '@completion/selectors';
import { State } from '@completion/reducers';
import { GlobalSearchResultDialogComponent } from '../context-toolbar/global-search-result-dialog/global-search-result-dialog.component';
import { SidenavStateService } from '../core/services/sidenav-state.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeInAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('{{ delay }}')], {
        delay: '0.1s 3000ms ease-in-out',
      }),
    ]),
  ],
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  public animationTime = '0.1s 0ms ease-in-out';
  public manualActive$: Observable<boolean> | undefined;
  isOpen: boolean;

  /** Short, upper-cased environment label rendered inside the brand chip ("PROD", "DEV", …). */
  public readonly envLabel: string = (env.get().name || '').toUpperCase();

  @Input()
  akerTitle = '';

  @Input()
  titleClickUrl = '';

  @Input()
  hideUserLogin = false;

  @Input()
  logoClickUrl = 'https://www.akersolutions.com/';

  @HostBinding('class.app-header')
  classAppHeader = true;
  private loaderConfig: LoaderConfig = defaultLoaderConfig;
  public isLoading: Observable<boolean>;

  /** Bound to the popover-based search input. */
  public searchValue = '';
  public searchManualDialogVisible = false;

  private readonly destroy$ = new Subject<void>();
  private globalSearchDialogRef: MatDialogRef<GlobalSearchResultDialogComponent> | null = null;

  constructor(
    private loaderService: LoaderService,
    private readonly stompService: StompWebSocketService,
    private readonly userManualService: UserManualService,
    private readonly store: Store<State>,
    private readonly dialog: MatDialog,
    public readonly sidenavState: SidenavStateService,
  ) {
    this.isLoading = this.loaderService.isLoadingStream.pipe(delay(0), shareReplay(1));
    this.manualActive$ = this.userManualService.manualActive$;

    this.isOpen = this.sidenavState.isOpen;
    this.sidenavState.isOpen$.pipe(takeUntil(this.destroy$)).subscribe((open) => (this.isOpen = open));
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (env.get().featureFlags.stomp) {
      this.stompService.disconnect();
    }
  }

  public async ngOnInit(): Promise<void> {
    if (this.loaderConfig?.delayLoadbar != null) {
      this.setDelayForLoadbar(this.loaderConfig.delayLoadbar);
    }

    // Use the same project-scoped global search as the context toolbar.
    this.store
      .select(getGlobalSearchResponse)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (!response || this.globalSearchDialogRef) return;
        this.globalSearchDialogRef = this.dialog.open(GlobalSearchResultDialogComponent, {
          width: 'min(1180px, 96vw)',
          maxWidth: '98vw',
          maxHeight: '90vh',
          data: response,
        });
        this.globalSearchDialogRef
          .afterClosed()
          .pipe(first())
          .subscribe(() => {
            this.globalSearchDialogRef = null;
            this.clearSearch();
          });
      });

    if (env.get().featureFlags.stomp) {
      // TODO look into this when we have have the
      // this.stompService.connect();
      //
      // this.stompService.incomingMessages$
      //   .pipe(takeUntil(this.destroy$))
      //   .subscribe(msg => {
      //     console.log('WebSocket notification:', msg.body);
      //   });
    }
  }

  private setDelayForLoadbar(delay: number): void {
    const next = `0.1s ${delay}ms ease-in-out`;
    if (this.animationTime === next) return; // no change
    // Defer assignment to a microtask so initial CD pass finishes before value changes.
    Promise.resolve().then(() => {
      this.animationTime = next;
      // If needed we could markForCheck; default change detection will pick this up.
    });
  }

  /** Triggers a project-scoped global search using the popover value. */
  public submitSearch(): void {
    const term = (this.searchValue || '').trim();
    if (!term) return;
    this.store.dispatch(new GlobalSearch(term));
  }

  /** Clears the current search input and any pending search result. */
  public clearSearch(): void {
    this.searchValue = '';
    this.store.dispatch(new SearchTagsSuccess([]));
    this.store.dispatch(new ClearGlobalSearch());
  }

  /** Opens the static user manual for global search. */
  public openSearchManual(): void {
    this.searchManualDialogVisible = true;
  }

  /** Toggle the side-navigation drawer from the top bar hamburger button. */
  public toggleSidenav(): void {
    this.sidenavState.toggle();
  }
}
